import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Check, ChevronDown, FileText } from 'lucide-react';
import { cn } from '../../../../../../utils/classnames';
import { camelizeKeys } from '../../../../../../utils/camelizeKeys';
import { useToast } from '../../../../../../components/ui/toast/ToastContext';

const FORMAT_OPTIONS = [{ value: 'pdf', label: 'PDF' }];

const SECTION_OPTIONS = [
  {
    value: 'politica_nacional',
    label: 'Estratégia de investimentos (nacional)',
  },
  {
    value: 'politica_internacional',
    label: 'Estratégia de investimentos (internacional)',
  },
  {
    value: 'alocacao_nacional',
    label: 'Alocação Patrimonial (nacional)',
  },
  {
    value: 'alocacao_internacional',
    label: 'Alocação Patrimonial (internacional)',
  },
];

const normalizeErrors = (error) => {
  if (error?.errors?.length) return error.errors.join(', ');
  if (error?.error) return error.error;
  if (error?.message) return error.message;

  return 'Ocorreu um erro inesperado';
};

const formatDate = (value) => {
  if (!value) return '-';

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return '-';

  return date.toLocaleDateString('pt-BR');
};

const getFormatLabel = (format) => {
  const normalizedFormat = String(format || '').toLowerCase();

  if (normalizedFormat === 'ppt' || normalizedFormat === 'pptx') return 'Apresentação (ppt)';
  if (normalizedFormat === 'pdf') return 'PDF';

  return format || '-';
};

const buildDefaultReportName = () => {
  const now = new Date();

  const month = now.toLocaleDateString('pt-BR', {
    month: 'long',
  });

  const formattedMonth = month.charAt(0).toUpperCase() + month.slice(1);
  const year = String(now.getFullYear()).slice(-2);

  return `Relatório - ${formattedMonth}/${year}`;
};

const ReportCheckbox = ({ checked, onChange, label }) => {
  return (
    <button type="button" onClick={onChange} className="flex items-center gap-3 text-left group">
      <span
        className={cn(
          'flex items-center justify-center shrink-0 border border-bluePale rounded-md bg-white transition-colors',
          checked ? 'text-tealBase' : 'text-transparent group-hover:border-tealBase'
        )}
        style={{ width: 20, height: 20 }}
      >
        <Check size={14} strokeWidth={2.25} />
      </span>

      <span className="text-base text-blueDarker">{label}</span>
    </button>
  );
};

const FormInput = ({ label, name, value, onChange }) => {
  return (
    <div className="flex flex-col gap-2 min-w-0">
      <label htmlFor={name} className="text-sm text-blueSteel">
        {label}
      </label>

      <input
        id={name}
        name={name}
        value={value || ''}
        onChange={onChange}
        className="w-full bg-white border border-bluePale rounded-xl px-5 py-3 text-base text-blueDarker outline-none transition-colors focus:border-tealBase"
      />
    </div>
  );
};

const FormatSelect = ({ value, onChange }) => {
  return (
    <div className="flex flex-col gap-2 min-w-0">
      <label htmlFor="format" className="text-sm text-blueSteel">
        Formato
      </label>

      <div className="relative">
        <select
          id="format"
          name="format"
          value={value}
          onChange={onChange}
          className="appearance-none w-full bg-white border border-bluePale rounded-xl px-5 py-3 pr-11 text-base text-blueDarker outline-none transition-colors focus:border-tealBase"
        >
          {FORMAT_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        <div className="pointer-events-none absolute inset-y-0 right-4 flex items-center">
          <ChevronDown size={18} className="text-blueSteel" strokeWidth={1.5} />
        </div>
      </div>
    </div>
  );
};

const ExportForm = ({ form, exporting, onChangeField, onToggleSection, onSubmit }) => {
  return (
    <div className="bg-blueLight rounded-xl p-5">
      <div className="grid gap-5" style={{ gridTemplateColumns: '1fr 0.46fr' }}>
        <FormInput label="Nome do relatório" name="name" value={form.name} onChange={onChangeField} />
        <FormatSelect value={form.format} onChange={onChangeField} />
      </div>

      <div className="mt-5 flex items-end justify-between gap-8">
        <div className="flex flex-col gap-5">
          <span className="text-sm text-blueSteel">Dados inclusos</span>

          <div className="grid gap-x-14 gap-y-4" style={{ gridTemplateColumns: '1fr 1fr' }}>
            {SECTION_OPTIONS.map((section) => (
              <ReportCheckbox
                key={section.value}
                label={section.label}
                checked={form.sections.includes(section.value)}
                onChange={() => onToggleSection(section.value)}
              />
            ))}
          </div>
        </div>

        <button
          type="button"
          disabled={exporting}
          onClick={onSubmit}
          className={cn(
            'bg-tealMedium text-white text-sm font-semibold rounded-xl px-7 py-3 transition-colors border-r-2 border-b-2 border-tealBase shrink-0',
            exporting ? 'opacity-50 cursor-not-allowed' : 'hover:bg-tealDark'
          )}
        >
          {exporting ? 'EXPORTANDO...' : 'EXPORTAR DADOS'}
        </button>
      </div>
    </div>
  );
};

const ExportedReportRow = ({ report, deleting, onDelete, canWrite }) => {
  const canOpenFile = !!report.fileUrl;

  return (
    <div className="bg-blueLight rounded-xl px-5 py-3">
      <div
        className="grid items-center gap-5 text-sm text-blueSteel"
        style={{ gridTemplateColumns: '42px 1.6fr 0.9fr 1.1fr auto auto auto' }}
      >
        <div
          className="flex items-center justify-center bg-white text-tealBase rounded-full shrink-0"
          style={{ width: 40, height: 40 }}
        >
          <FileText size={21} strokeWidth={1.5} />
        </div>

        <div className="min-w-0">
          <div className="truncate text-base font-semibold text-blueDarker" title={report.name}>
            {report.name || '-'}
          </div>

          <div className="truncate italic text-sm text-blueSteel" title={report.filename}>
            {report.filename || '-'}
          </div>
        </div>

        <div className="truncate">{getFormatLabel(report.format)}</div>

        <div className="truncate">Gerado em {formatDate(report.generatedAt)}</div>

        {canOpenFile ? (
          <a
            href={report.fileUrl}
            target="_blank"
            rel="noreferrer"
            className="text-base text-tealBase font-medium underline hover:opacity-75 whitespace-nowrap"
          >
            Ver relatório
          </a>
        ) : (
          <span className="text-blueSteel whitespace-nowrap">Sem arquivo</span>
        )}

        {canOpenFile ? (
          <a
            href={report.fileUrl}
            download={report.filename || true}
            className="text-base text-tealBase font-medium underline hover:opacity-75 whitespace-nowrap"
          >
            Baixar
          </a>
        ) : (
          <span className="text-blueSteel whitespace-nowrap">Baixar</span>
        )}

        {canWrite ? (
          <button
            type="button"
            disabled={deleting}
            onClick={() => onDelete(report)}
            className={cn(
              'text-base text-redSoft font-medium underline transition-opacity whitespace-nowrap',
              deleting ? 'opacity-40 cursor-not-allowed' : 'hover:opacity-75'
            )}
          >
            Excluir
          </button>
        ) : (
          <span />
        )}
      </div>
    </div>
  );
};

const ExportReports = ({ clientId, scope, canWrite }) => {
  const { addToast } = useToast();

  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const [form, setForm] = useState({
    name: buildDefaultReportName(),
    format: 'pdf',
    sections: ['politica_nacional'],
  });

  const getCsrfToken = () => document.querySelector('meta[name="csrf-token"]')?.content;

  const baseUrl = useMemo(() => {
    return `/app/planner/client/${clientId}/dashboard/investment_strategy/exported_reports`;
  }, [clientId]);

  const buildUrl = useCallback(
    (suffix = '') => {
      const query = scope ? `?scope=${encodeURIComponent(scope)}` : '';

      return `${baseUrl}${suffix}${query}`;
    },
    [baseUrl, scope]
  );

  const fetchReports = useCallback(() => {
    setLoading(true);

    fetch(buildUrl())
      .then(async (res) => {
        const responseData = await res.json();

        if (!res.ok) {
          throw responseData;
        }

        return responseData;
      })
      .then((responseData) => {
        const camelData = camelizeKeys(responseData);

        setReports(camelData.exportedReports || []);
      })
      .catch((error) => {
        console.error('Erro ao carregar relatórios exportados:', error);

        addToast({
          type: 'error',
          message: 'Erro ao carregar relatórios exportados',
        });

        setReports([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [addToast, buildUrl]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const handleChangeField = (event) => {
    const { name, value } = event.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleToggleSection = (section) => {
    setForm((prev) => {
      const alreadySelected = prev.sections.includes(section);

      return {
        ...prev,
        sections: alreadySelected ? prev.sections.filter((item) => item !== section) : [...prev.sections, section],
      };
    });
  };

  const validateForm = () => {
    if (!form.name?.trim()) {
      addToast({
        type: 'error',
        message: 'Informe o nome do relatório',
      });

      return false;
    }

    if (!form.sections.length) {
      addToast({
        type: 'error',
        message: 'Selecione pelo menos um dado para exportar',
      });

      return false;
    }

    return true;
  };

  const handleCreateReport = async () => {
    if (!validateForm()) return;

    setExporting(true);

    try {
      const response = await fetch(buildUrl(), {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCsrfToken(),
        },
        body: JSON.stringify({
          exported_report: {
            name: form.name,
            format: form.format,
            sections: form.sections,
          },
        }),
      });

      const responseData = await response.json();

      if (!response.ok) {
        throw responseData;
      }

      const camelData = camelizeKeys(responseData);

      setReports((prev) => [camelData.exportedReport, ...prev]);

      addToast({
        type: 'success',
        message: 'Relatório exportado com sucesso',
      });
    } catch (error) {
      console.error('Erro ao exportar relatório:', error);

      addToast({
        type: 'error',
        message: normalizeErrors(error),
      });
    } finally {
      setExporting(false);
    }
  };

  const handleDeleteReport = async (report) => {
    setDeletingId(report.id);

    try {
      const response = await fetch(buildUrl(`/${report.id}`), {
        method: 'DELETE',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCsrfToken(),
        },
      });

      const responseData = await response.json();

      if (!response.ok) {
        throw responseData;
      }

      setReports((prev) => prev.filter((item) => item.id !== report.id));

      addToast({
        type: 'success',
        message: 'Relatório excluído com sucesso',
      });
    } catch (error) {
      console.error('Erro ao excluir relatório:', error);

      addToast({
        type: 'error',
        message: normalizeErrors(error),
      });
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="flex flex-col gap-5">
      {canWrite && (
        <div className="bg-white rounded-custom20 px-10 py-8 flex flex-col gap-6 min-w-0">
          <h3 className="text-xl font-semibold text-blueDarker">Exportar dados</h3>

          <ExportForm
            form={form}
            exporting={exporting}
            onChangeField={handleChangeField}
            onToggleSection={handleToggleSection}
            onSubmit={handleCreateReport}
          />
        </div>
      )}

      <div className="bg-white rounded-custom20 px-10 py-8 flex flex-col gap-6 min-w-0">
        <h3 className="text-xl font-semibold text-blueDarker">Relatórios exportados</h3>

        <div className="flex flex-col gap-5 overflow-y-auto pr-1" style={{ maxHeight: 430 }}>
          {loading ? (
            <div className="bg-blueLight rounded-xl px-5 py-5 text-blueSteel">Carregando relatórios exportados...</div>
          ) : reports.length === 0 ? (
            <div className="bg-blueLight rounded-xl px-5 py-5 text-blueSteel">Nenhum relatório exportado ainda.</div>
          ) : (
            reports.map((report) => (
              <ExportedReportRow
                key={report.id}
                report={report}
                deleting={deletingId === report.id}
                onDelete={handleDeleteReport}
                canWrite={canWrite}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default ExportReports;
